// src/pages/Utilisateurs/PrepaPhysique/components/PrepaPhysiqueToolbar.jsx

import React, { useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaRedo,
  FaSearch,
  FaSlidersH,
} from "react-icons/fa";

import {
  DEFAULT_PREPA_PHYSIQUE_STATE,
  STATUS_OPTIONS,
  hasActivePrepaFilters,
} from "../utils/prepaPhysiqueUtils";

export default function PrepaPhysiqueToolbar({
  ui,
  dynamicOptions,
  searchInput,
  setSearchInput,
  onChange,
  onReset,
  filteredCount,
  totalExercises,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilters = useMemo(() => hasActivePrepaFilters(ui), [ui]);

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Rechercher un exercice..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold shadow-sm transition active:scale-95 md:hidden ${
            filtersOpen
              ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900"
              : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
          }`}
        >
          <FaSlidersH />
          <span>Filtres</span>
          {filtersOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} adhérent{filteredCount > 1 ? "s" : ""} ·{" "}
          {totalExercises} exercice{totalExercises > 1 ? "s" : ""}
        </p>

        {activeFilters && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaRedo className="text-[11px]" />
            Reset
          </button>
        )}
      </div>

      <div className="mt-4 hidden grid-cols-2 gap-3 md:grid">
        <FiltersContent
          ui={ui}
          dynamicOptions={dynamicOptions}
          onChange={onChange}
        />
      </div>

      {filtersOpen && (
        <div className="mt-3 md:hidden">
          <div className="max-h-[calc(100dvh-180px)] overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-inner dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  Affichage
                </p>
                <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                  Filtrer les exercices
                </h3>
              </div>

              {activeFilters && (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  Réinitialiser
                </button>
              )}
            </div>

            <div className="space-y-3">
              <FiltersContent
                ui={ui}
                dynamicOptions={dynamicOptions}
                onChange={onChange}
                stacked
              />
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              Voir les résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FiltersContent({ ui, dynamicOptions, onChange, stacked = false }) {
  const wrapperClass = stacked ? "space-y-3" : "contents";

  return (
    <div className={wrapperClass}>
      <label className="block">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          État
        </span>

        <div className="grid grid-cols-3 gap-2 rounded-2xl bg-slate-100 p-1 dark:bg-slate-950">
          {STATUS_OPTIONS.map((option) => {
            const active = ui.status === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ status: option.value })}
                className={`min-h-10 rounded-xl px-2 text-xs font-bold transition active:scale-95 ${
                  active
                    ? "bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950"
                    : "text-slate-500 hover:bg-white dark:text-slate-400 dark:hover:bg-slate-800"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </label>

      <FilterSelect
        label="Programme"
        value={ui.programme}
        options={dynamicOptions.programmes}
        onChange={(value) => onChange({ programme: value })}
      />
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option === DEFAULT_PREPA_PHYSIQUE_STATE.programme
              ? "Tous les programmes"
              : option}
          </option>
        ))}
      </select>
    </label>
  );
}